import { useState, useContext } from 'react'
import {
  useMutationCheckCardCanDelete,
  useMutationDeleteCard,
  useMutationUpdateCard,
  useQueryGetCards
} from '../../../../../../queries/paymentQueries'
import { useQueryGetUserMe } from '../../../../../../queries/userQueries'
import { Plus } from 'lucide-react'
import { ToastContext } from '../../../../../../context/ToastContext'
import CardItem from './CardItem'
import RefundModal from './RefundModal'
import MercadoPagoPaymentModal from '../../../../payment/MercadoPagoPaymentModal'
import CardDeleteConflictModal from './CardDeleteConflictModal'
import { VALIDATION_AMOUNT } from '../../../../../utils/constant'

const SavedCardsList = () => {
  const { data: userMe } = useQueryGetUserMe()
  const organizationId = userMe?.currentOrganizationId || null
  const { data: cardsData, isLoading } = useQueryGetCards()
  const cards = cardsData?.cards || []
  const customerId = cardsData?.customerId || null
  const cardsIds = (cards || [])
    .map((card) => card.mercadopagoCardId)
    .filter((id) => typeof id === 'string' && id.trim() !== '')
  const [selectedCard, setSelectedCard] = useState(null)
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState(null)
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [conflictModal, setConflictModal] = useState(null)
  const { showToast } = useContext(ToastContext)

  const checkCanDelete = useMutationCheckCardCanDelete()
  const deleteCard = useMutationDeleteCard()
  const updateCard = useMutationUpdateCard()

  const handleCheckCanDelete = async (cardId) => {
    return checkCanDelete.mutateAsync(cardId)
  }

  const handleDeleteConflict = (card, result) => {
    setConflictModal({
      card,
      message: result.message || 'La tarjeta tiene suscripciones activas.',
      subscriptions: result.subscriptions || [],
      addonSubscriptions: result.addonSubscriptions || []
    })
  }

  const handleDelete = async (cardId) => {
    try {
      const result = await deleteCard.mutateAsync(cardId)
      if (result?.success) {
        showToast('success', 'Tarjeta eliminada correctamente')
        setConflictModal(null)
      }
    } catch (error) {
      showToast('error', 'Error al eliminar la tarjeta')
    }
  }

  const handleEdit = async (cardId, newName) => {
    try {
      await updateCard.mutateAsync({ cardId, name: newName })
      showToast('success', 'Tarjeta actualizada correctamente')
    } catch (error) {
      showToast('error', 'Error al actualizar la tarjeta')
    }
  }

  const handleRefund = (card, payment) => {
    setSelectedCard(card)
    setSelectedPayment(payment)
    setIsRefundModalOpen(true)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-accent1"></div>
      </div>
    )
  }

  const handlePaymentSuccess = () => {
    setIsPaymentModalOpen(false)
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setIsPaymentModalOpen(true)}
          className="bg-primary text-white px-6 py-2 rounded-full hover:bg-primary-dark shadow hover:shadow-lg hover:bg-opacity-90 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          <span>Agregar Tarjeta</span>
        </button>
      </div>

      {cards.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600 mb-4">No tienes tarjetas guardadas</p>
          <p className="text-sm text-gray-500">
            Haz clic en &quot;Agregar Tarjeta&quot; para agregar una nueva tarjeta
          </p>
        </div>
      ) : (

        cards.map((card) => (
          <CardItem
            key={card.id}
            card={card}
            onCheckCanDelete={handleCheckCanDelete}
            onDelete={handleDelete}
            onDeleteConflict={handleDeleteConflict}
            onEdit={handleEdit}
            onRefund={handleRefund}
          />
        ))
      )}

      <RefundModal
        isOpen={isRefundModalOpen}
        onClose={() => {
          setIsRefundModalOpen(false)
          setSelectedCard(null)
          setSelectedPayment(null)
        }}
        card={selectedCard}
        payment={selectedPayment}
        onSuccess={() => {
          setIsRefundModalOpen(false)
          setSelectedCard(null)
          setSelectedPayment(null)
        }}
      />

      <MercadoPagoPaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        amount={VALIDATION_AMOUNT}
        onSuccess={handlePaymentSuccess}
        customerId={customerId}
        cardsIds={cardsIds}
      />

      {conflictModal && (
        <CardDeleteConflictModal
          open
          onClose={() => setConflictModal(null)}
          card={conflictModal.card}
          message={conflictModal.message}
          subscriptions={conflictModal.subscriptions}
          addonSubscriptions={conflictModal.addonSubscriptions}
          organizationId={organizationId}
          customerId={customerId}
          cardsIds={cardsIds}
        />
      )}
    </div>
  )
}

export default SavedCardsList

